import { useEffect, useState } from "react";
import { useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, MessageCircle, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearSession, saveSession } from "@/lib/auth";
import { LogoMark } from "@/lib/Logo";
import { BRAND, OWNER } from "@/lib/constants";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, closed: 0, revenue: 0 });
  const [drafts, setDrafts] = useState({});
  const [staff, setStaff] = useState({ name: "", email: "", password: "" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [list, sum] = await Promise.all([api.get("/inquiries"), api.get("/inquiries/stats")]);
    setInquiries(list.data);
    setStats(sum.data);
    setDrafts(Object.fromEntries(list.data.map((i) => [i.id, { status: i.status, notes: i.notes, expected_amount: i.expected_amount }])));
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        if (r.data.role === "guest") {
          navigate("/account");
          return;
        }
        setUser(r.data);
        load().catch(() => toast.error("Could not load inquiries"));
      })
      .catch(() => setUser(false));
  }, [load, navigate]);

  const logout = async () => {
    clearSession();
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/admin/login");
  };

  const updateDraft = (id, key, value) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: value } }));

  const save = async (id) => {
    const draft = drafts[id];
    try {
      await api.patch(`/inquiries/${id}`, {
        status: draft.status,
        notes: draft.notes,
        expected_amount: Number(draft.expected_amount) || 0,
      });
      toast.success("Inquiry updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/staff", staff);
      toast.success(`Staff account created for ${data.email}`);
      setStaff({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (user === null) return <div className="admin-loading" data-testid="admin-loading">Loading inbox…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <div className="brand">
          <LogoMark size={30} />
          <span>
            <strong>{BRAND}</strong>
            <small>Owner inbox · {user.name}{user.role === "staff" ? " (staff)" : ""}</small>
          </span>
        </div>
        <div className="admin-actions">
          <button className="button button-ghost" onClick={load} data-testid="refresh-inquiries-button">
            <RefreshCw size={15} /> Refresh
          </button>
          <Link to="/" className="button button-ghost" data-testid="view-site-link">View site</Link>
          <button className="button button-dark" onClick={logout} data-testid="admin-logout-button">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="stats-row" data-testid="stats-row">
        {[
          ["Total inquiries", stats.total, "stat-total"],
          ["Open", stats.open, "stat-open"],
          ["Deals closed", stats.closed, "stat-closed"],
          ["Revenue (closed)", `₹${Number(stats.revenue).toLocaleString("en-IN")}`, "stat-revenue"],
        ].map(([label, value, testid]) => (
          <div className="stat-card" key={testid} data-testid={testid}>
            <small>{label}</small>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      {user.role === "admin" && (
        <form className="staff-form" onSubmit={addStaff} data-testid="add-staff-form">
          <div className="staff-form-head">
            <UserPlus size={18} />
            <strong>Add a team member</strong>
            <span>They sign in at /admin/login and see this same inbox.</span>
          </div>
          <input required value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} placeholder="Name" data-testid="staff-name-input" />
          <input required type="email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} placeholder="Email" data-testid="staff-email-input" />
          <input required type="password" minLength={8} value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} placeholder="Password (min 8)" data-testid="staff-password-input" />
          <button className="button button-gold" type="submit" data-testid="staff-submit-button">Add staff</button>
        </form>
      )}

      {inquiries.length === 0 ? (
        <div className="empty-state" data-testid="empty-inbox">
          No inquiries yet. New enquiries from the website will appear here automatically.
        </div>
      ) : (
        <div className="inquiry-list">
          {inquiries.map((inq) => {
            const draft = drafts[inq.id] || { status: inq.status, notes: "", expected_amount: 0 };
            return (
              <article className={`inquiry-card ${draft.status}`} key={inq.id} data-testid={`inquiry-card-${inq.id}`}>
                <div className="inquiry-head">
                  <div>
                    <strong data-testid={`inquiry-name-${inq.id}`}>{inq.guest_name}</strong>
                    <span className="inquiry-meta">
                      {inq.mobile} · {fmtDate(inq.created_at)}
                    </span>
                  </div>
                  <span className={`status-badge ${draft.status}`} data-testid={`inquiry-status-${inq.id}`}>
                    {draft.status === "closed" ? "Deal closed" : "Open"}
                  </span>
                </div>
                <div className="inquiry-details">
                  <span><b>Room:</b> {inq.room}</span>
                  <span><b>Guests:</b> {inq.guests}</span>
                  <span><b>Check-in:</b> {inq.check_in || "—"}</span>
                  <span><b>Check-out:</b> {inq.check_out || "—"}</span>
                  <span><b>Area:</b> {inq.area || "—"}</span>
                  <span><b>Breakfast:</b> {inq.breakfast}</span>
                  <span><b>Transfer:</b> {inq.transfer === "Yes" ? `${inq.vehicle} · ${inq.transfer_type}` : "No"}</span>
                  {inq.special && <span><b>Request:</b> {inq.special}</span>}
                </div>
                <a
                  className="wa-link"
                  href={`https://wa.me/${inq.mobile.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`inquiry-whatsapp-${inq.id}`}
                >
                  <MessageCircle size={14} /> Chat with guest
                </a>
                <div className="inquiry-controls">
                  <label>
                    Status
                    <select value={draft.status} onChange={(e) => updateDraft(inq.id, "status", e.target.value)} data-testid={`inquiry-status-select-${inq.id}`}>
                      <option value="open">Open</option>
                      <option value="closed">Deal closed</option>
                    </select>
                  </label>
                  <label>
                    Expected amount (₹)
                    <input type="number" min="0" value={draft.expected_amount} onChange={(e) => updateDraft(inq.id, "expected_amount", e.target.value)} data-testid={`inquiry-amount-input-${inq.id}`} />
                  </label>
                  <label className="wide">
                    Notes
                    <textarea value={draft.notes} onChange={(e) => updateDraft(inq.id, "notes", e.target.value)} placeholder="Internal notes — hotel name, advance received, etc." data-testid={`inquiry-notes-input-${inq.id}`} />
                  </label>
                  <button className="button button-gold" onClick={() => save(inq.id)} data-testid={`inquiry-save-${inq.id}`}>
                    Save
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
